/**
 * Simple playback state management (FikFap-style)
 */
class PlaybackManager {
    constructor() {
        this.video = null;
        this.state = {
            playing: false,
            paused: true,
            currentTime: 0,
            duration: 0,
            muted: true,
            loading: false,
            ended: false,
        };
        this.callbacks = {
            onStateChange: null,
            onTimeUpdate: null,
            onEnded: null,
            onError: null,
        };

        // Track pending play promise to allow cancellation
        this.pendingPlayPromise = null;
        this.isDestroying = false;
    }

    /**
     * Set video element
     */
    setVideo(video) {
        if (this.video) {
            this.removeEventListeners();
        }
        
        this.video = video;
        
        if (video) {
            this.setupEventListeners();
            this.updateState({
                paused: video.paused,
                playing: !video.paused,
                currentTime: video.currentTime,
                duration: video.duration || 0,
                muted: video.muted,
            });
        }
    }

    /**
     * Play video (FikFap-style: simple and trust browser/HLS.js)
     */
    async play() {
        if (!this.video || this.isDestroying) return false;

        // Cancel any pending play promise before starting new one
        if (this.pendingPlayPromise) {
            this.pendingPlayPromise = null;
        }

        try {
            this.updateState({ loading: true });

            // Handle play promise properly
            const playPromise = this.video.play();
            this.pendingPlayPromise = playPromise;

            if (playPromise !== undefined) {
                await playPromise;
                this.pendingPlayPromise = null;
                console.log('[PlaybackManager] Play succeeded');
            }

            return true;
        } catch (error) {
            this.pendingPlayPromise = null;

            // If we're destroying, AbortError is expected and not an error
            if (this.isDestroying && error.name === 'AbortError') {
                return false;
            }

            console.error('[PlaybackManager] Play failed:', {
                name: error.name,
                message: error.message,
                videoReadyState: this.video?.readyState,
                videoNetworkState: this.video?.networkState,
            });
            this.updateState({ loading: false });

            // Simple error callback - no recovery attempts
            this.callbacks.onError?.({
                type: error.name,
                error: error,
                fatal: false
            });

            return false;
        }
    }

    /**
     * Pause video
     */
    pause() {
        if (!this.video) return;

        // Cancel any pending play promise
        if (this.pendingPlayPromise) {
            this.pendingPlayPromise = null;
        }

        this.video.pause();
    }

    /**
     * Toggle play/pause
     */
    togglePlay() {
        if (!this.video) return;
        
        if (this.video.paused) {
            this.play();
        } else {
            this.pause();
        }
    }

    /**
     * Toggle mute
     */
    toggleMute() {
        if (!this.video) return;
        this.video.muted = !this.video.muted;
    }

    /**
     * Seek to time
     */
    seek(time) {
        if (!this.video || !isFinite(time)) return;
        this.video.currentTime = Math.max(0, Math.min(time, this.video.duration || 0));
    }

    /**
     * Reset playback
     */
    reset(initialTime = 0) {
        if (!this.video) return;
        
        this.pause();
        this.seek(initialTime);
        this.updateState({
            playing: false,
            paused: true,
            ended: false,
            loading: false,
        });
    }

    /**
     * Get current state
     */
    getState() {
        return { ...this.state };
    }

    /**
     * Update state and notify
     */
    updateState(updates) {
        const changed = Object.keys(updates).some(
            key => this.state[key] !== updates[key]
        );
        
        if (changed) {
            Object.assign(this.state, updates);
            this.callbacks.onStateChange?.(this.getState());
        }
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        if (!this.video) return;

        const handlers = {
            play: () => this.updateState({ playing: true, paused: false, loading: false }),
            pause: () => this.updateState({ playing: false, paused: true }),
            ended: () => {
                this.updateState({ playing: false, paused: true, ended: true });
                this.callbacks.onEnded?.();
            },
            timeupdate: () => {
                this.updateState({
                    currentTime: this.video.currentTime,
                    duration: this.video.duration || 0,
                });
                this.callbacks.onTimeUpdate?.({
                    currentTime: this.video.currentTime,
                    duration: this.video.duration || 0,
                });
            },
            loadedmetadata: () => {
                this.updateState({
                    duration: this.video.duration || 0,
                    loading: false,
                });
            },
            // Simplified waiting handling - trust HLS.js buffer management
            waiting: () => {
                // Only show loading for initial load (currentTime = 0)
                if (this.video.currentTime === 0) {
                    this.updateState({ loading: true });
                }
            },
            playing: () => this.updateState({ loading: false, playing: true, paused: false }),
            canplay: () => this.updateState({ loading: false }),
            canplaythrough: () => this.updateState({ loading: false }),
            loadstart: () => {
                // Only show loading for initial load
                if (this.video.currentTime === 0) {
                    this.updateState({ loading: true });
                }
            },
            volumechange: () => this.updateState({ muted: this.video.muted }),
            error: (e) => {
                console.error('[PlaybackManager] Video error:', {
                    error: e,
                    errorCode: e.target?.error?.code,
                    errorMessage: e.target?.error?.message,
                    currentSrc: this.video?.currentSrc,
                    readyState: this.video?.readyState,
                    networkState: this.video?.networkState,
                });
                this.updateState({ loading: false });
                this.callbacks.onError?.(e);
            },
        };

        this._handlers = handlers;
        
        Object.entries(handlers).forEach(([event, handler]) => {
            this.video.addEventListener(event, handler);
        });
    }

    /**
     * Remove event listeners
     */
    removeEventListeners() {
        if (!this.video || !this._handlers) return;

        Object.entries(this._handlers).forEach(([event, handler]) => {
            this.video.removeEventListener(event, handler);
        });

        this._handlers = null;
    }

    /**
     * Set callback
     */
    on(event, callback) {
        if (event in this.callbacks) {
            this.callbacks[event] = callback;
        }
    }

    /**
     * Cleanup
     */
    destroy() {
        // Mark as destroying to suppress AbortError logging
        this.isDestroying = true;

        // Cancel any pending play promise
        if (this.pendingPlayPromise) {
            this.pendingPlayPromise = null;
        }

        this.removeEventListeners();
        this.video = null;
        this.state = {
            playing: false,
            paused: true,
            currentTime: 0,
            duration: 0,
            muted: true,
            loading: false,
            ended: false,
        };

        // Reset destroying flag after cleanup
        this.isDestroying = false;
    }
}

export default PlaybackManager;